import { Disposable, IDisposable, toDisposable } from "../../../../base/common/lifecycle";
import { safeStringify } from "../../../../base/common/objects";
import { MenuId, MenuRegistry } from "../../../../platform/actions/common/actions";
import { IAnalyticsService } from "../../../../platform/analytics/common/analytics";
import { ContextKeyLoggedIn as IsLoggedInContextKey } from "../../../../platform/auth/common/auth";
import { Command, CommandsRegistry, ICommandCanExecuteReturnValue, ICommandService } from "../../../../platform/commands/common/commands";
import { ContextKeyExpr, IContextKeyService } from "../../../../platform/contextkey/common/contextkey";
import { HierarchyNode } from "../../../../platform/hierarchy/common/hierarchyService";
import { ServicesAccessor } from "../../../../platform/instantiation/common/instantiation";
import { createDecorator } from "../../../../platform/instantiation/common/registry";
import { ILogService } from "../../../../platform/log/common/log";
import { INotificationService } from "../../../../platform/notifications/common/notifications";
import { IPropertyAccessorService } from "../../../../platform/properties/common/accessor";
import { IProperty, ValueMetadata } from "../../../../platform/properties/common/properties";

export const IChangesetService = createDecorator<IChangesetService>('changesetService');

export interface IChangesetService {
    readonly _serviceBrand: undefined;

    registerPropertyChange(property: IProperty, oldValue?: any): void;
    registerChange(path: string, newValue: unknown, oldValue?: unknown, metadata?: ValueMetadata): void;

    exportChanges(format: ChangeFormat): Promise<CompiledChanges>;

}

type ChangeFormat = "json" | "text";
type CompiledChanges = { text: string, empty?: false } | { text: null, empty: true };

interface RegisteredPropertyData {
    property: IProperty;
    path: string;
    key: string;
    oldValue: any;
    newValue: any;
    registeredAt?: number;
    lastModifiedAt?: number;
}

export class ChangesetService extends Disposable implements IChangesetService {

    readonly _serviceBrand: undefined;

    constructor(
        @ILogService private readonly logger: ILogService,
        @IPropertyAccessorService private readonly propertyAccessorService: IPropertyAccessorService,
        @IContextKeyService contextKeyService: IContextKeyService,
        @IAnalyticsService private readonly analyticsService: IAnalyticsService,
    ) {
        super();
        this._register(toDisposable(() => {
            this.registeredProperties.clear();
        }));
        const changeserviceKey = contextKeyService.createKey('changesetServiceRegistered', true);
        this._register(toDisposable(() => {
            changeserviceKey.reset();
        }));
    }

    private registeredProperties: Map<string, RegisteredPropertyData> = new Map();

    registerPropertyChange(property: IProperty, previousValue?: any): void {

        // Check if already registered and update
        const path = this.tryGetPath(property, property.owner) || (property.owner as any).name || '<unknown>';
        const key = path + '.' + property.key;

        const prevRegistered = this.registeredProperties.get(key);
        // We're only interested in the first old value so we can show the full change at the end.
        // At export time we can also filter out no-op changes.
        const newValue = property.value;
        let oldValue = prevRegistered?.oldValue !== undefined ? prevRegistered.oldValue : previousValue;
        const registeredAt = prevRegistered?.registeredAt || Date.now();
        const lastModifiedAt = Date.now();
        if (!prevRegistered) {
            oldValue = previousValue !== undefined ? previousValue : property.cloneValue();
            this.logger.debug(`ChangesetService: Registering new property change as ${key} from ${oldValue} to ${newValue}`);
        }
        else this.logger.debug(`ChangesetService: Property change registered as: ${key} from ${oldValue} to ${newValue}`);
        this.registeredProperties.set(key, {
            property: property,
            path: path!,
            key: property.key,
            oldValue: oldValue,
            newValue: newValue,
            registeredAt,
            lastModifiedAt
        });
    }

    private registeredChanges: Map<string, { path: string, oldValue: any, newValue: any, metadata?: unknown }> = new Map();

    registerChange(path: string, newValue: unknown, oldValue?: unknown, metadata?: unknown): void {
        const key = path;

        const prevRegistered = this.registeredChanges.get(key);
        // We're only interested in the first old value so we can show the full change at the end.
        // At export time we can also filter out no-op changes.
        let initialValue = prevRegistered?.oldValue !== undefined ? prevRegistered.oldValue : oldValue;
        if (!prevRegistered) {
            initialValue = oldValue;
            this.logger.debug(`ChangesetService: Registering new change as ${key} from ${initialValue} to ${newValue}`);
        }
        else this.logger.debug(`ChangesetService: Change registered as: ${key} from ${initialValue} to ${newValue}`);
        this.registeredChanges.set(key, {
            path,
            oldValue: initialValue,
            newValue,
            metadata
        });
    }

    async exportChanges(format: ChangeFormat): Promise<CompiledChanges> {

        // Gather only changed properties
        const changedProperties = new Map<string, RegisteredPropertyData>();
        const otherChanges = new Map<string, { path: string, oldValue: any, newValue: any, metadata?: unknown }>();

        // Filter out no-op changes
        this.registeredProperties.forEach((change, key) => {
            if (!change.property.equalsValue(change.oldValue, change.newValue)) {
                changedProperties.set(key, change);
            }
        });

        this.registeredChanges.forEach((change, key) => {
            if (change.oldValue !== change.newValue) {
                otherChanges.set(key, change);
            }
        });

        if (changedProperties?.size === 0 && otherChanges.size === 0) {
            this.logger.info("ChangesetService: No changes to export.");
            this.analyticsService?.logEvent('changeset.exported', { format, changeCount: 0 });
            return { empty: true, text: null }
        }

        this.analyticsService?.logEvent('changeset.exported', { format, changeCount: changedProperties.size });

        const url = typeof window !== undefined ? window.location.href : undefined;

        // TODO: need a better way to ensure serialized values are not too large (e.g. textures). Maybe we only need the metadata if we have it?

        switch (format) {
            case "text": {
                let result = `${changedProperties.size} changed properties in ${url}\n\n`;
                for (const [, change] of changedProperties) {
                    const info = await this.getChangeInfo(change);
                    result += `Path: ${change.path}`;
                    if (info.id) {
                        result += `, ID: ${info.id}`;
                    }
                    result += `\nProperty: ${change.key}`;
                    if (info.oldValueMetadata) {
                        result += `\n- Old: ${safeStringify(info.oldValueMetadata)}`;
                    }
                    else {
                        result += `\n- Old: ${safeStringify(info.oldValue)}`;
                    }
                    if (info.newValueMetadata) {
                        result += `\n- New: ${safeStringify(info.newValueMetadata)}`;
                    }
                    else {
                        result += `\n- New: ${safeStringify(info.newValue)}`;
                    }
                    result += `\n\n`;
                }
                for (const [, change] of otherChanges) {
                    result += `Path: ${change.path}\n`;
                    if (change.metadata) {
                        result += `- New: ${safeStringify(change.metadata)}\n`;
                    }
                    else {
                        result += `- Old Value: ${safeStringify(change.oldValue)}\n`;
                        result += `- New Value: ${safeStringify(change.newValue)}\n`;
                    }
                    result += `\n`;
                }
                return { text: result };
            }

            case "json": {
                const json = {
                    url: url,
                    exportedAt: Date.now(),
                    changes: {}
                }
                for (const [, change] of changedProperties) {
                    const info = await this.getChangeInfo(change);
                    json.changes[`${change.path}.${change.key}`] = info;
                };
                for (const [, change] of otherChanges) {
                    const newValueMetadata = change.metadata;
                    json.changes[change.path] = {
                        metadata: newValueMetadata ? { ...newValueMetadata } : undefined,
                        oldValue: newValueMetadata ? undefined : change.oldValue,
                        newValue: newValueMetadata ? undefined : change.newValue,
                    }
                };
                return { text: safeStringify(json) };
            }
        }

        throw new Error(`Unsupported change format: ${format}`);
    }

    private async getChangeInfo(change: RegisteredPropertyData) {
        const node = HierarchyNode.get(change.property.owner);
        let id: string | undefined = undefined;
        let name: string | undefined = undefined;
        if (node) {
            id = node.id;
            name = node.name;
        }
        const metadatas = await Promise.allSettled([
            this.propertyAccessorService.readMetadata?.(change.oldValue),
            this.propertyAccessorService.readMetadata?.(change.newValue)
        ]);
        const oldMetadataStr = metadatas[0].status === "fulfilled" ? metadatas[0].value : undefined;
        const newMetadataStr = metadatas[1].status === "fulfilled" ? metadatas[1].value : undefined;
        return {
            id,
            registeredAt: change.registeredAt,
            lastModifiedAt: change.lastModifiedAt !== change.registeredAt ? change.lastModifiedAt : undefined,
            // putting metadata first so it gets serialized first and we clone it so if it exists multiple times we don't serialize with [Circular]
            oldValueMetadata: oldMetadataStr ? {...oldMetadataStr } : undefined,
            newValueMetadata: newMetadataStr ? {...newMetadataStr } : undefined,
            oldValue: oldMetadataStr ? undefined : change.oldValue,
            newValue: newMetadataStr ? undefined : change.newValue,
        };
    }

    private tryGetPath(property: IProperty, object: object): string {
        let path = "";

        if (property.metadata.address) {
            return property.metadata.address;
        }

        // TODO: we need a better way to address properties 
        // since these could be props in materials or components...
        // so properties should ideally provide their own addressing scheme

        // first try traversing parents if available
        while ("parent" in object) {
            const obj = object as any;
            path = `/${obj.name || obj.type}${path}`;
            if (!obj.parent) break;
            object = obj.parent;
        }

        // fallback is traversing the node hierarchy
        if (!path?.length) {
            let node = HierarchyNode.get(object);
            if (node) {
                while (node) {
                    path = `/${node.name || (`${node.type}:${node.id}`)}${path}`;
                    node = node.parent;
                }
                return path;
            }
        }

        if ("id" in object) {
            const obj = object as any;
            path = `/${obj.id}`;
            if ("type" in object) {
                path += ` (${obj.name || obj.type})`;
            }
        }

        return path;
    }
}


class GetChangesCommand extends Command {
    static readonly ID = 'changeset.download';

    constructor() {
        super(GetChangesCommand.ID, 'Get Changes', {
            description: 'Get the current changeset as text',
        });
    }

    canExecute(args: unknown): ICommandCanExecuteReturnValue {
        return { canExecute: true };
    }

    async execute(accessor: ServicesAccessor, args: { format: ChangeFormat }) {
        const changesetService = accessor.get(IChangesetService);
        const notificationService = accessor.getIfExists(INotificationService);
        const format = args?.format || "text";
        const changes = await changesetService.exportChanges(format);
        if (changes.empty === true) {
            notificationService?.showNotification({
                message: 'No changes recorded. Property modifications are tracked. Only actual changes are included in the changeset.',
                type: 'warning',
                position: "cursor"
            });
            return;
        }
        const extension = format === "json" ? "json" : "txt";

        const text = changes.text as string;
        const blob = new Blob([text], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `changeset.${extension}`;
        a.click();
        URL.revokeObjectURL(url);
    }
}
CommandsRegistry.registerCommand(new GetChangesCommand());

const downloadChangesetTxtId = "changeset.download.txt";
CommandsRegistry.registerCommand(downloadChangesetTxtId, (accessor: ServicesAccessor) => {
    const commandService = accessor.getIfExists(ICommandService);
    return commandService?.executeCommand(GetChangesCommand.ID, { format: "text" });
});
const downloadChangesetJsonId = "changeset.download.json";
CommandsRegistry.registerCommand(downloadChangesetJsonId, (accessor: ServicesAccessor) => {
    const commandService = accessor.getIfExists(ICommandService);
    return commandService?.executeCommand(GetChangesCommand.ID, { format: "json" });
});

MenuRegistry.appendMenuItem(MenuId.MenubarFileExportMenu, {
    command: {
        id: downloadChangesetTxtId,
        title: 'Edits (Text)',
        icon: { id: "export" },
        tooltip: 'Download your changes in the editor as a text file. Can be used to review or share your edits or to pass them into an LLM.',
        // precondition: ContextKeyExpr.equals(IsLoggedInContextKey, true),
        // toggled: {
        //     condition: ContextKeyExpr.equals(IsLoggedInContextKey, false),
        //     title: 'Download Edits as Text (Requires Login)',
        // }
    },
    order: 1,
    when: ContextKeyExpr.equals('changesetServiceRegistered', true),
});

MenuRegistry.appendMenuItem(MenuId.MenubarFileExportMenu, {
    command: {
        id: downloadChangesetJsonId,
        title: 'Edits (JSON)',
        icon: { id: "export" },
        tooltip: 'Download your changes in the editor as a JSON file. Can be used to review or share your edits or to pass them into an LLM.',
        // precondition: ContextKeyExpr.equals(IsLoggedInContextKey, true),
        // toggled: {
        //     condition: ContextKeyExpr.equals(IsLoggedInContextKey, false),
        //     title: 'Download Edits as JSON (Requires Login)',
        // }
    },
    order: 1,
    when: ContextKeyExpr.equals('changesetServiceRegistered', true),
});